import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  ChevronRight,
  ChevronLeft,
  Check,
  Sparkles,
  AlertCircle,
  Loader,
  Upload,
} from 'lucide-react';
import type { Pet } from '../../types/pet';
import { usePetPhoto } from '../../hooks/usePetPhoto';
import { apiClient, getApiErrorMessage } from '../../../../api/axios';
import { mintNFT } from '../../../../api/nft-router';

interface MintNFTFlowProps {
  pet: Pet;
  onClose: () => void;
  onComplete: (nft: unknown) => void;
}

const TEMPLATE_OPTIONS = [
  { id: 'cartoon', emoji: '🎨', labelKey: 'nft.mint.cartoon' as const },
  { id: 'pixel', emoji: '🕹️', labelKey: 'nft.mint.pixelArt' as const },
  { id: 'sticker', emoji: '✨', labelKey: 'nft.mint.sticker' as const },
  { id: 'seasonal', emoji: '🎃', labelKey: 'nft.mint.seasonal' as const },
];

/** Inline styles for preview so filters/borders apply reliably (no Tailwind arbitrary values). */
function getArtworkImageStyle(artworkType: string): React.CSSProperties {
  switch (artworkType) {
    case 'cartoon':
      return { filter: 'contrast(1.1) saturate(1.2)' };
    case 'pixel':
      return {};
    case 'sticker':
      return {
        filter: 'contrast(1.12)',
        border: '12px solid white',
        boxSizing: 'border-box',
      };
    case 'seasonal':
      return { filter: 'sepia(0.2) saturate(1.1)' };
    default:
      return {};
  }
}

/** Seasonal: 6 options with inline background + filter so preview matches theme. */
const SEASON_THEMES: {
  id: string;
  labelKey: string;
  style: React.CSSProperties;
  imageFilter?: string;
}[] = [
  {
    id: 'christmas',
    labelKey: 'nft.mint.seasonChristmas',
    style: {
      background:
        'linear-gradient(135deg, #166534 0%, #b91c1c 50%, #15803d 100%)',
    },
    imageFilter: 'sepia(0.15) saturate(1.1)',
  },
  {
    id: 'halloween',
    labelKey: 'nft.mint.seasonHalloween',
    style: {
      background:
        'linear-gradient(135deg, #7c2d12 0%, #6b21a8 50%, #431407 100%)',
    },
    imageFilter: 'sepia(0.25) saturate(1.15)',
  },
  {
    id: 'easter',
    labelKey: 'nft.mint.seasonEaster',
    style: {
      background:
        'linear-gradient(135deg, #f9a8d4 0%, #e9d5ff 50%, #c4b5fd 100%)',
    },
    imageFilter: 'saturate(1.1) brightness(1.05)',
  },
  {
    id: 'winter',
    labelKey: 'nft.mint.seasonWinter',
    style: {
      background:
        'linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 50%, #bae6fd 100%)',
    },
    imageFilter: 'sepia(0.1) saturate(0.95) brightness(1.08)',
  },
  {
    id: 'summer',
    labelKey: 'nft.mint.seasonSummer',
    style: {
      background:
        'linear-gradient(135deg, #fef08a 0%, #fde047 50%, #fbbf24 100%)',
    },
    imageFilter: 'sepia(0.1) saturate(1.2)',
  },
  {
    id: 'rain',
    labelKey: 'nft.mint.seasonRain',
    style: {
      background:
        'linear-gradient(135deg, #64748b 0%, #475569 50%, #0ea5e9 100%)',
    },
    imageFilter: 'sepia(0.2) saturate(0.9) brightness(0.95)',
  },
];

const MINT_SIZE = 512;

/**
 * Generate the minted NFT image with canvas so Cartoon/Pixel/Sticker/Seasonal match preview.
 * - Cartoon: contrast(1.1) saturate(1.2)
 * - Pixel: draw small then scale up with imageSmoothingEnabled = false
 * - Sticker: draw image then white stroke rect
 * - Seasonal: sepia(0.2) saturate(1.1); theme filter applied via CSS on export is optional
 */
async function generateMintImageBlob(
  imageUrl: string,
  artworkType: string,
  seasonThemeId?: string
): Promise<Blob | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = MINT_SIZE;
        canvas.height = MINT_SIZE;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }

        const isCartoon = artworkType === 'cartoon';
        const isPixel = artworkType === 'pixel';
        const isSticker = artworkType === 'sticker';
        const isSeasonal = artworkType === 'seasonal';

        if (isPixel) {
          const small = 32;
          ctx.imageSmoothingEnabled = false;
          ctx.drawImage(img, 0, 0, small, small);
          const temp = document.createElement('canvas');
          temp.width = MINT_SIZE;
          temp.height = MINT_SIZE;
          const tctx = temp.getContext('2d');
          if (!tctx) {
            resolve(null);
            return;
          }
          tctx.imageSmoothingEnabled = false;
          tctx.drawImage(
            canvas,
            0,
            0,
            small,
            small,
            0,
            0,
            MINT_SIZE,
            MINT_SIZE
          );
          ctx.drawImage(temp, 0, 0);
        } else {
          ctx.drawImage(img, 0, 0, MINT_SIZE, MINT_SIZE);
        }

        if (isSticker) {
          const borderPx = 12;
          ctx.strokeStyle = 'white';
          ctx.lineWidth = borderPx * 2;
          ctx.strokeRect(
            borderPx,
            borderPx,
            MINT_SIZE - borderPx * 2,
            MINT_SIZE - borderPx * 2
          );
        }

        const applyFilter = (filter: string) => {
          const temp = document.createElement('canvas');
          temp.width = MINT_SIZE;
          temp.height = MINT_SIZE;
          const tctx = temp.getContext('2d');
          if (!tctx) return;
          tctx.drawImage(canvas, 0, 0);
          ctx.filter = filter;
          ctx.drawImage(temp, 0, 0);
          ctx.filter = 'none';
        };

        if (isCartoon) {
          applyFilter('contrast(1.1) saturate(1.2)');
        }
        if (isSeasonal) {
          const theme = SEASON_THEMES.find(
            (s) => s.id === (seasonThemeId ?? 'christmas')
          );
          applyFilter(theme?.imageFilter ?? 'sepia(0.2) saturate(1.1)');
        }

        canvas.toBlob((blob) => resolve(blob ?? null), 'image/png', 0.92);
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = imageUrl;
  });
}

export function MintNFTFlow({ pet, onClose, onComplete }: MintNFTFlowProps) {
  const { t } = useTranslation();
  const { signedImageUrl: profilePhotoUrl } = usePetPhoto({
    petId: pet.id,
    photoKey: pet.photoKey,
    imageUrl: pet.imageUrl,
  });
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const [currentStep, setCurrentStep] = useState<
    'disclosure' | 'artwork' | 'customize' | 'mint' | 'minting' | 'success' | 'error'
  >('disclosure');
  const [mintError, setMintError] = useState<string | null>(null);
  const [mintResult, setMintResult] = useState<{
    id?: string;
    explorerUrl?: string;
    nftMintAddress?: string;
    txSignature?: string;
    name?: string;
    network?: string;
    message?: string;
  } | null>(null);
  /** Tracks which sub-step of the mint flow is in progress */
  const [mintSubStep, setMintSubStep] = useState<
    'preparing' | 'uploading' | 'minting_solana' | 'confirming'
  >('preparing');
  const [understood, setUnderstood] = useState(false);
  const [artworkType, setArtworkType] = useState<
    'profile' | 'upload' | 'cartoon' | 'pixel' | 'sticker' | 'seasonal'
  >('profile');
  const [, setUploadedFile] = useState<File | null>(null);
  const [uploadedPreviewUrl, setUploadedPreviewUrl] = useState<string | null>(
    null
  );
  const [frame, setFrame] = useState('none');
  const [background, setBackground] = useState('gradient-blue');
  const [caption, setCaption] = useState('');
  const [nickname, setNickname] = useState(pet.name);
  const [seasonTheme, setSeasonTheme] = useState('christmas');

  // Resolve the image URL to show in previews: profile photo, uploaded photo, or profile as base for templates
  const displayImageUrl =
    artworkType === 'upload' ? uploadedPreviewUrl : (profilePhotoUrl ?? null);

  const isTemplate =
    (artworkType as string) in
    { cartoon: 1, pixel: 1, sticker: 1, seasonal: 1 };
  const templateLabel = isTemplate
    ? TEMPLATE_OPTIONS.find((tpl) => tpl.id === artworkType)?.labelKey
    : null;

  // Revoke object URL when uploaded file changes or component unmounts
  useEffect(() => {
    return () => {
      if (uploadedPreviewUrl) URL.revokeObjectURL(uploadedPreviewUrl);
    };
  }, [uploadedPreviewUrl]);

  const artworkImageStyle = getArtworkImageStyle(artworkType);
  const isSticker = artworkType === 'sticker';
  const isPixel = artworkType === 'pixel';
  const isSeasonal = artworkType === 'seasonal';
  const seasonThemeConfig = SEASON_THEMES.find((s) => s.id === seasonTheme);
  const previewBgStyle: React.CSSProperties =
    isSeasonal && seasonThemeConfig ? seasonThemeConfig.style : {};
  const previewBgClass = !isSeasonal
    ? background === 'gradient-blue'
      ? 'bg-gradient-to-br from-blue-400 to-purple-400'
      : background === 'gradient-pink'
        ? 'bg-gradient-to-br from-pink-400 to-orange-400'
        : background === 'gradient-green'
          ? 'bg-gradient-to-br from-green-400 to-teal-400'
          : 'bg-gradient-to-br from-gray-400 to-gray-600'
    : '';
  const seasonalImageFilter =
    isSeasonal && seasonThemeConfig?.imageFilter
      ? seasonThemeConfig.imageFilter
      : 'sepia(0.2) saturate(1.1)';

  const handleUploadFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    if (uploadedPreviewUrl) URL.revokeObjectURL(uploadedPreviewUrl);
    setUploadedFile(file);
    setUploadedPreviewUrl(URL.createObjectURL(file));
    e.target.value = '';
  };

  const canProceedFromArtwork =
    artworkType !== 'upload' || !!uploadedPreviewUrl;

  const handleDisclosureAccept = () => {
    if (understood) {
      setCurrentStep('artwork');
    }
  };

  const handleMint = async () => {
    setCurrentStep('minting');
    setMintError(null);
    setMintSubStep('preparing');

    try {
      // 1. Generate artwork image blob
      let mintedImageBlob: Blob | null = null;
      const imageUrl = displayImageUrl ?? undefined;
      const isTemplateType =
        artworkType === 'cartoon' ||
        artworkType === 'pixel' ||
        artworkType === 'sticker' ||
        artworkType === 'seasonal';
      if (imageUrl && isTemplateType) {
        mintedImageBlob = await generateMintImageBlob(
          imageUrl,
          artworkType,
          artworkType === 'seasonal' ? seasonTheme : undefined
        );
      }

      // If no generated blob (profile or upload type), create one from the display image
      if (!mintedImageBlob && imageUrl) {
        const resp = await fetch(imageUrl);
        mintedImageBlob = await resp.blob();
      }

      if (!mintedImageBlob) {
        throw new Error('Failed to generate artwork image');
      }

      // 2. Upload image to R2 via documents upload
      setMintSubStep('uploading');
      const safeName = `nft-${Date.now()}.png`;
      const initRes = await apiClient.post<{
        uploadUrl: string;
        key: string;
      }>('/documents/upload-init', {
        filename: safeName,
        contentType: 'image/png',
        folder: 'nft',
        ttlSeconds: 900,
      });

      let uploadUrl = initRes.data.uploadUrl;
      if (uploadUrl.startsWith('/')) {
        const base = apiClient.defaults.baseURL ?? '';
        uploadUrl = `${base}${uploadUrl}`;
      }

      await apiClient.put(uploadUrl, mintedImageBlob, {
        headers: { 'Content-Type': 'image/png' },
      });

      const imageKey = initRes.data.key;

      // 3. Call the NFT mint API (backend handles the on-chain tx — Solana
      //    or Cardano depending on PUBLIC_CHAIN_PROVIDER, the router picks).
      setMintSubStep('minting_solana');
      const result = await mintNFT({
        petId: pet.id,
        imageKey,
        artworkType,
        frame: frame || undefined,
        background: background || undefined,
        caption: caption || undefined,
        seasonTheme: artworkType === 'seasonal' ? seasonTheme : undefined,
      });

      // 4. Transaction confirmed
      setMintSubStep('confirming');

      // 5. Success — map unified router fields to the local Solana-shaped state.
      const localResult = {
        id: result.id,
        explorerUrl: result.explorerUrl,
        nftMintAddress: result.assetId,
        txSignature: result.txHash,
        name: result.name,
        network: result.network,
        message: result.message,
      };
      setMintResult(localResult);
      setCurrentStep('success');
      onComplete(localResult);
    } catch (err: unknown) {
      console.error('NFT mint failed:', err);
      // Use getApiErrorMessage to properly extract backend error messages from axios errors
      const message = getApiErrorMessage(err, 'Minting failed. Please try again.');
      setMintError(message);
      setCurrentStep('error');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-pink-500 to-purple-500 text-white p-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Sparkles className="w-8 h-8" />
              <div>
                <h2 className="text-2xl mb-1">{t('nft.mint.title')}</h2>
                <p className="text-sm opacity-90">{t('nft.mint.subtitle')}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {/* Step 0: Disclosure */}
          {currentStep === 'disclosure' && (
            <div className="max-w-2xl mx-auto space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-10 h-10 text-yellow-600" />
                </div>
                <h3 className="text-2xl mb-2">{t('nft.mint.beforeYouMint')}</h3>
                <p className="text-gray-600">
                  {t('nft.mint.readImportantInfo')}
                </p>
              </div>

              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-6">
                <h4 className="text-lg mb-4">
                  {t('nft.mint.funCollectibleOnly')}
                </h4>
                <div className="space-y-3 text-gray-700">
                  <p className="flex items-start gap-2">
                    <span className="text-yellow-600 mt-1">•</span>
                    <span>{t('nft.mint.doesNotRepresent')}</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="text-yellow-600 mt-1">•</span>
                    <span>{t('nft.mint.noConnection')}</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="text-yellow-600 mt-1">•</span>
                    <span>{t('nft.mint.noUtility')}</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="text-yellow-600 mt-1">•</span>
                    <span>{t('nft.mint.sharableCollectible')}</span>
                  </p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="mb-4">{t('nft.mint.whatThisMeans')}</h4>
                <div className="space-y-2 text-sm text-gray-700">
                  <div className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 mt-0.5" />
                    <span>{t('nft.mint.canShareSocial')}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 mt-0.5" />
                    <span>{t('nft.mint.canTransferSell')}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 mt-0.5" />
                    <span>{t('nft.mint.yoursToCollect')}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <X className="w-5 h-5 text-red-600 mt-0.5" />
                    <span>{t('nft.mint.notProveOwnership')}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <X className="w-5 h-5 text-red-600 mt-0.5" />
                    <span>{t('nft.mint.notHealthRecords')}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <X className="w-5 h-5 text-red-600 mt-0.5" />
                    <span>{t('nft.mint.notSpecialAccess')}</span>
                  </div>
                </div>
              </div>

              <label className="flex items-start gap-3 p-4 bg-gray-50 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-100">
                <input
                  type="checkbox"
                  checked={understood}
                  onChange={(e) => setUnderstood(e.target.checked)}
                  className="w-5 h-5 text-pink-600 rounded mt-1"
                />
                <span className="flex-1">{t('nft.mint.iUnderstand')}</span>
              </label>

              <button
                onClick={handleDisclosureAccept}
                disabled={!understood}
                className="w-full px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('nft.mint.continueToCreate')}
              </button>
            </div>
          )}

          {/* Step 1: Artwork Selection */}
          {currentStep === 'artwork' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl mb-2">{t('nft.mint.chooseArtwork')}</h3>
                <p className="text-gray-600">{t('nft.mint.selectHowLook')}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <label
                  className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                    artworkType === 'profile'
                      ? 'border-pink-500 bg-pink-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="artwork"
                    value="profile"
                    checked={artworkType === 'profile'}
                    onChange={(e) =>
                      setArtworkType(e.target.value as typeof artworkType)
                    }
                    className="sr-only"
                  />
                  <div className="text-center">
                    <div className="w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-xl mx-auto mb-4 flex items-center justify-center overflow-hidden">
                      {profilePhotoUrl ? (
                        <img
                          src={profilePhotoUrl}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-5xl">
                          {pet.species === 'dog' ? '🐕' : '🐈'}
                        </span>
                      )}
                    </div>
                    <h4 className="mb-2">{t('nft.mint.useProfilePhoto')}</h4>
                    <p className="text-sm text-gray-600">
                      {t('nft.mint.currentPhoto')}
                    </p>
                  </div>
                </label>

                <div
                  className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                    artworkType === 'upload'
                      ? 'border-pink-500 bg-pink-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onClick={() => setArtworkType('upload')}
                  onKeyDown={(e) =>
                    e.key === 'Enter' && setArtworkType('upload')
                  }
                  role="button"
                  tabIndex={0}
                >
                  <input
                    type="radio"
                    name="artwork"
                    value="upload"
                    checked={artworkType === 'upload'}
                    onChange={(e) =>
                      setArtworkType(e.target.value as typeof artworkType)
                    }
                    className="sr-only"
                  />
                  <input
                    ref={uploadInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleUploadFileChange}
                    className="sr-only"
                    aria-hidden
                  />
                  <div className="text-center">
                    <div className="w-32 h-32 bg-gray-100 rounded-xl mx-auto mb-4 flex items-center justify-center overflow-hidden">
                      {uploadedPreviewUrl && artworkType === 'upload' ? (
                        <img
                          src={uploadedPreviewUrl}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-4xl">📸</span>
                      )}
                    </div>
                    <h4 className="mb-2">{t('nft.mint.uploadNewPhoto')}</h4>
                    <p className="text-sm text-gray-600 mb-3">
                      {t('nft.mint.chooseDifferentImage')}
                    </p>
                    {artworkType === 'upload' && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          uploadInputRef.current?.click();
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:border-pink-400 hover:text-pink-600 transition-colors"
                      >
                        <Upload className="w-4 h-4" />
                        {uploadedPreviewUrl
                          ? t('nft.mint.changePhoto')
                          : t('nft.mint.choosePhoto')}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h4 className="mb-4">{t('nft.mint.orChooseTemplate')}</h4>
                <div className="grid grid-cols-4 gap-3">
                  {TEMPLATE_OPTIONS.map((template) => (
                    <label
                      key={template.id}
                      className={`border-2 rounded-lg p-4 cursor-pointer text-center transition-all ${
                        artworkType === template.id
                          ? 'border-pink-500 bg-pink-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <input
                        type="radio"
                        name="artwork"
                        value={template.id}
                        checked={artworkType === template.id}
                        onChange={(e) =>
                          setArtworkType(e.target.value as typeof artworkType)
                        }
                        className="sr-only"
                      />
                      <div className="text-3xl mb-2">{template.emoji}</div>
                      <div className="text-sm">{t(template.labelKey)}</div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Template preview: show photo with selected style so user sees cartoon/pixel/sticker/seasonal before next step */}
              {isTemplate && displayImageUrl && (
                <div className="rounded-xl border-2 border-gray-200 p-4 bg-gray-50">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    {t('nft.mint.livePreviewNextStep')}
                  </h4>
                  <div className="flex justify-center">
                    <div
                      className={`rounded-xl overflow-hidden p-4 ${!isSeasonal ? 'bg-white' : ''}`}
                      style={
                        isSeasonal && seasonThemeConfig
                          ? seasonThemeConfig.style
                          : undefined
                      }
                    >
                      {isSticker ? (
                        <div
                          className="w-24 h-24 rounded-lg overflow-hidden flex items-center justify-center bg-gray-100"
                          style={{
                            filter: 'contrast(1.12)',
                            border: '12px solid white',
                            boxSizing: 'border-box',
                          }}
                        >
                          <img
                            src={displayImageUrl}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : isPixel ? (
                        <div
                          className="overflow-hidden rounded-lg"
                          style={{
                            width: 128,
                            height: 128,
                            imageRendering: 'pixelated',
                          }}
                        >
                          <div
                            style={{
                              width: 32,
                              height: 32,
                              transform: 'scale(4)',
                              transformOrigin: 'top left',
                            }}
                          >
                            <img
                              src={displayImageUrl}
                              alt=""
                              style={{
                                width: 32,
                                height: 32,
                                objectFit: 'cover',
                                display: 'block',
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="w-24 h-24 rounded-lg overflow-hidden">
                          <img
                            src={displayImageUrl}
                            alt=""
                            className="w-full h-full object-cover"
                            style={
                              isSeasonal && seasonThemeConfig?.imageFilter
                                ? { filter: seasonThemeConfig.imageFilter }
                                : artworkImageStyle
                            }
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  {templateLabel && (
                    <p className="text-center text-xs text-gray-500 mt-2">
                      {t(templateLabel)}
                    </p>
                  )}
                </div>
              )}

              {!isTemplate && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                  {t('nft.mint.livePreviewNextStep')}
                </div>
              )}

              {artworkType === 'upload' && !uploadedPreviewUrl && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                  {t('nft.mint.pleaseChoosePhoto')}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Customize */}
          {currentStep === 'customize' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl mb-2">
                    {t('nft.mint.customizeTitle')}
                  </h3>
                  <p className="text-gray-600">
                    {t('nft.mint.purelyCosmetic')}
                  </p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded px-3 py-1 text-sm text-yellow-800">
                  {t('nft.mint.cosmeticOnly')}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Preview */}
                <div>
                  <h4 className="mb-3">{t('nft.mint.livePreview')}</h4>
                  <div
                    className={`aspect-square rounded-xl p-8 ${previewBgClass}`}
                    style={
                      Object.keys(previewBgStyle).length
                        ? previewBgStyle
                        : undefined
                    }
                  >
                    <div
                      className={`w-full h-full rounded-lg bg-white flex items-center justify-center relative overflow-hidden ${
                        frame === 'border'
                          ? 'border-8 border-yellow-400'
                          : frame === 'shadow'
                            ? 'shadow-2xl'
                            : frame === 'neon'
                              ? 'border-4 border-pink-400 shadow-lg shadow-pink-400/50'
                              : ''
                      }`}
                    >
                      <div className="text-center w-full h-full flex flex-col items-center justify-center p-4">
                        {displayImageUrl ? (
                          <div className="relative w-32 h-32 sm:w-40 sm:h-40 rounded-xl overflow-hidden mb-3 flex-shrink-0 flex items-center justify-center">
                            {isSticker ? (
                              <div
                                className="rounded-xl overflow-hidden w-full h-full flex items-center justify-center bg-gray-100"
                                style={{
                                  filter: 'contrast(1.12)',
                                  border: '12px solid white',
                                  boxSizing: 'border-box',
                                }}
                              >
                                <img
                                  src={displayImageUrl}
                                  alt=""
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ) : isPixel ? (
                              <div
                                className="overflow-hidden rounded-xl flex-shrink-0"
                                style={{
                                  width: 128,
                                  height: 128,
                                  imageRendering: 'pixelated',
                                }}
                              >
                                <div
                                  style={{
                                    width: 32,
                                    height: 32,
                                    transform: 'scale(4)',
                                    transformOrigin: 'top left',
                                  }}
                                >
                                  <img
                                    src={displayImageUrl}
                                    alt=""
                                    style={{
                                      width: 32,
                                      height: 32,
                                      objectFit: 'cover',
                                      display: 'block',
                                    }}
                                  />
                                </div>
                              </div>
                            ) : (
                              <img
                                src={displayImageUrl}
                                alt=""
                                className="w-full h-full object-cover"
                                style={
                                  isSeasonal
                                    ? { filter: seasonalImageFilter }
                                    : artworkImageStyle
                                }
                              />
                            )}
                            {templateLabel && (
                              <span className="absolute bottom-1 left-1 right-1 bg-black/60 text-white text-xs py-1 rounded text-center">
                                {t(templateLabel)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="relative inline-block">
                            <div className="text-6xl mb-4">
                              {pet.species === 'dog' ? '🐕' : '🐈'}
                            </div>
                            {templateLabel && (
                              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-2 py-0.5 rounded whitespace-nowrap">
                                {t(templateLabel)}
                              </span>
                            )}
                          </div>
                        )}
                        <div className="text-xl mb-2">{nickname}</div>
                        {caption && (
                          <div className="text-sm text-gray-600 italic">
                            "{caption}"
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Customization Options */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm mb-2">
                      {t('nft.mint.nicknameOptional')}
                    </label>
                    <input
                      type="text"
                      value={nickname}
                      onChange={(e) => setNickname(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder={pet.name}
                      maxLength={20}
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      {t('nft.mint.notOfficialName')}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm mb-2">
                      {t('nft.mint.funCaptionOptional')}
                    </label>
                    <input
                      type="text"
                      value={caption}
                      onChange={(e) => setCaption(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder={t('nft.mint.captionPlaceholder')}
                      maxLength={30}
                    />
                  </div>

                  <div>
                    <label className="block text-sm mb-2">
                      {t('nft.mint.frameStyle')}
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: 'none', labelKey: 'nft.mint.frameNone' as const },
                        {
                          id: 'border',
                          labelKey: 'nft.mint.frameGoldBorder' as const,
                        },
                        {
                          id: 'shadow',
                          labelKey: 'nft.mint.frameShadow' as const,
                        },
                        { id: 'neon', labelKey: 'nft.mint.frameNeon' as const },
                      ].map((f) => (
                        <button
                          key={f.id}
                          onClick={() => setFrame(f.id)}
                          className={`px-3 py-2 rounded-lg border-2 transition-all ${
                            frame === f.id
                              ? 'border-pink-500 bg-pink-50'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          {t(f.labelKey)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {isSeasonal && (
                    <div>
                      <label className="block text-sm mb-2">
                        {t('nft.mint.chooseSeasonalTheme')}
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {SEASON_THEMES.map((s) => (
                          <button
                            key={s.id}
                            onClick={() => setSeasonTheme(s.id)}
                            className={`px-3 py-2 rounded-lg border-2 transition-all ${
                              seasonTheme === s.id
                                ? 'border-pink-500 bg-pink-50'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                          >
                            <div
                              className="w-full h-6 rounded mb-1"
                              style={s.style}
                            />
                            <div className="text-xs">{t(s.labelKey)}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm mb-2">
                      {t('nft.mint.background')}
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        {
                          id: 'gradient-blue',
                          labelKey: 'nft.mint.bgBluePurple' as const,
                          color: 'from-blue-400 to-purple-400',
                        },
                        {
                          id: 'gradient-pink',
                          labelKey: 'nft.mint.bgPinkOrange' as const,
                          color: 'from-pink-400 to-orange-400',
                        },
                        {
                          id: 'gradient-green',
                          labelKey: 'nft.mint.bgGreenTeal' as const,
                          color: 'from-green-400 to-teal-400',
                        },
                        {
                          id: 'gradient-gray',
                          labelKey: 'nft.mint.bgGray' as const,
                          color: 'from-gray-400 to-gray-600',
                        },
                      ].map((bg) => (
                        <button
                          key={bg.id}
                          onClick={() => setBackground(bg.id)}
                          className={`px-3 py-2 rounded-lg border-2 transition-all ${
                            background === bg.id
                              ? 'border-pink-500'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <div
                            className={`w-full h-8 rounded bg-gradient-to-r ${bg.color} mb-1`}
                          />
                          <div className="text-xs">{t(bg.labelKey)}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Mint Details */}
          {currentStep === 'mint' && (
            <div className="max-w-2xl mx-auto space-y-6">
              <div className="text-center">
                <h3 className="text-2xl mb-2">{t('nft.mint.readyToMint')}</h3>
                <p className="text-gray-600">{t('nft.mint.reviewDetails')}</p>
              </div>

              {/* Preview */}
              <div
                className={`aspect-square max-w-sm mx-auto rounded-xl p-8 ${previewBgClass}`}
                style={
                  Object.keys(previewBgStyle).length
                    ? previewBgStyle
                    : undefined
                }
              >
                <div
                  className={`w-full h-full rounded-lg bg-white flex items-center justify-center overflow-hidden ${
                    frame === 'border'
                      ? 'border-8 border-yellow-400'
                      : frame === 'shadow'
                        ? 'shadow-2xl'
                        : frame === 'neon'
                          ? 'border-4 border-pink-400 shadow-lg shadow-pink-400/50'
                          : ''
                  }`}
                >
                  <div className="text-center w-full h-full flex flex-col items-center justify-center p-4">
                    {displayImageUrl ? (
                      <div className="relative w-32 h-32 sm:w-40 sm:h-40 rounded-xl overflow-hidden mb-3 flex-shrink-0 flex items-center justify-center">
                        {isSticker ? (
                          <div
                            className="rounded-xl overflow-hidden w-full h-full flex items-center justify-center bg-gray-100"
                            style={{
                              filter: 'contrast(1.12)',
                              border: '12px solid white',
                              boxSizing: 'border-box',
                            }}
                          >
                            <img
                              src={displayImageUrl}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : isPixel ? (
                          <div
                            className="overflow-hidden rounded-xl flex-shrink-0"
                            style={{
                              width: 128,
                              height: 128,
                              imageRendering: 'pixelated',
                            }}
                          >
                            <div
                              style={{
                                width: 32,
                                height: 32,
                                transform: 'scale(4)',
                                transformOrigin: 'top left',
                              }}
                            >
                              <img
                                src={displayImageUrl}
                                alt=""
                                style={{
                                  width: 32,
                                  height: 32,
                                  objectFit: 'cover',
                                  display: 'block',
                                }}
                              />
                            </div>
                          </div>
                        ) : (
                          <img
                            src={displayImageUrl}
                            alt=""
                            className="w-full h-full object-cover"
                            style={
                              isSeasonal
                                ? { filter: seasonalImageFilter }
                                : artworkImageStyle
                            }
                          />
                        )}
                        {templateLabel && (
                          <span className="absolute bottom-1 left-1 right-1 bg-black/60 text-white text-xs py-1 rounded text-center">
                            {t(templateLabel)}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="relative inline-block">
                        <div className="text-6xl mb-4">
                          {pet.species === 'dog' ? '🐕' : '🐈'}
                        </div>
                        {templateLabel && (
                          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-2 py-0.5 rounded whitespace-nowrap">
                            {t(templateLabel)}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="text-xl mb-2">{nickname}</div>
                    {caption && (
                      <div className="text-sm text-gray-600 italic">
                        "{caption}"
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Minting Cost */}
              <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <span className="text-xl">🪙</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-900">
                      {t('nft.mint.mintingCost', 'Minting Cost')}
                    </h4>
                    <p className="text-sm text-amber-700">
                      {t('nft.mint.mintingCostDesc', 'This will be deducted from your wallet')}
                    </p>
                  </div>
                </div>
                <div className="flex items-baseline gap-2 mt-3 ml-13">
                  <span className="text-3xl font-bold text-amber-900">50</span>
                  <span className="text-lg font-semibold text-amber-800">PETS</span>
                  <span className="text-sm text-amber-600 ml-2">
                    {t('nft.mint.tokensRequired', 'tokens required')}
                  </span>
                </div>
                <p className="text-xs text-amber-600 mt-2 ml-13">
                  {t('nft.mint.tokensNote', 'PETS tokens are transferred to the Petso treasury upon minting. Ensure you have claimed your rewards to your on-chain wallet.')}
                </p>
              </div>

              {/* Mint Details */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="mb-4">{t('nft.gallery.detailsTitle')}</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-gray-600">{t('nft.mint.network')}</div>
                    <div>Solana (Devnet)</div>
                  </div>
                  <div>
                    <div className="text-gray-600">{t('nft.mint.type')}</div>
                    <div>{t('nft.mint.typeCollectible')}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">{t('nft.mint.utility')}</div>
                    <div>{t('nft.mint.utilityNone')}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">
                      {t('nft.mint.royalties')}
                    </div>
                    <div>{t('nft.mint.royaltiesNone')}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">
                      {t('nft.mint.transferable')}
                    </div>
                    <div>{t('nft.mint.transferableYes')}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">
                      {t('nft.mint.collection')}
                    </div>
                    <div>{t('nft.mint.collectionName')}</div>
                  </div>
                </div>
              </div>

              <div className="bg-pink-50 border border-pink-200 rounded-lg p-4 text-sm text-pink-800">
                {t('nft.mint.reminderCosmetic')}
              </div>
            </div>
          )}

          {/* Minting Progress */}
          {currentStep === 'minting' && (
            <div className="max-w-md mx-auto text-center space-y-6 py-12">
              <div className="w-24 h-24 bg-gradient-to-br from-pink-100 to-purple-100 rounded-full flex items-center justify-center mx-auto">
                <Loader className="w-12 h-12 text-pink-600 animate-spin" />
              </div>

              <div>
                <h3 className="text-2xl mb-2">{t('nft.mint.mintingTitle')}</h3>
                <p className="text-gray-600">{t('nft.mint.mintingSubtitle')}</p>
              </div>

              {/* Cost reminder during minting */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 inline-flex items-center gap-2">
                <span>🪙</span>
                <span className="text-sm text-amber-800 font-medium">
                  {t('nft.mint.deducting50Pet', 'Deducting 50 PETS from your wallet')}
                </span>
              </div>

              <div className="space-y-3 text-left max-w-xs mx-auto">
                {/* Step 1: Preparing artwork */}
                <div className="flex items-center gap-3 text-sm">
                  {mintSubStep === 'preparing' ? (
                    <Loader className="w-5 h-5 text-pink-600 animate-spin flex-shrink-0" />
                  ) : (
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  )}
                  <span className={mintSubStep === 'preparing' ? 'text-pink-700 font-medium' : 'text-gray-600'}>
                    {t('nft.mint.preparingArtwork')}
                  </span>
                </div>

                {/* Step 2: Uploading image */}
                <div className="flex items-center gap-3 text-sm">
                  {mintSubStep === 'uploading' ? (
                    <Loader className="w-5 h-5 text-pink-600 animate-spin flex-shrink-0" />
                  ) : mintSubStep === 'preparing' ? (
                    <div className="w-5 h-5 border-2 border-gray-300 rounded-full flex-shrink-0" />
                  ) : (
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  )}
                  <span className={mintSubStep === 'uploading' ? 'text-pink-700 font-medium' : mintSubStep === 'preparing' ? 'text-gray-400' : 'text-gray-600'}>
                    {t('nft.mint.uploadingImage', 'Uploading artwork image')}
                  </span>
                </div>

                {/* Step 3: Minting on Solana */}
                <div className="flex items-center gap-3 text-sm">
                  {mintSubStep === 'minting_solana' ? (
                    <Loader className="w-5 h-5 text-pink-600 animate-spin flex-shrink-0" />
                  ) : mintSubStep === 'confirming' ? (
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  ) : (
                    <div className="w-5 h-5 border-2 border-gray-300 rounded-full flex-shrink-0" />
                  )}
                  <span className={mintSubStep === 'minting_solana' ? 'text-pink-700 font-medium' : mintSubStep === 'confirming' ? 'text-gray-600' : 'text-gray-400'}>
                    {t('nft.mint.mintingSolana')}
                  </span>
                </div>

                {/* Step 4: Confirming transaction */}
                <div className="flex items-center gap-3 text-sm">
                  {mintSubStep === 'confirming' ? (
                    <Loader className="w-5 h-5 text-pink-600 animate-spin flex-shrink-0" />
                  ) : (
                    <div className="w-5 h-5 border-2 border-gray-300 rounded-full flex-shrink-0" />
                  )}
                  <span className={mintSubStep === 'confirming' ? 'text-pink-700 font-medium' : 'text-gray-400'}>
                    {t('nft.mint.confirmingTx', 'Confirming Solana transaction')}
                  </span>
                </div>
              </div>

              <div className="text-xs text-gray-500">
                {mintSubStep === 'minting_solana' || mintSubStep === 'confirming'
                  ? t('nft.mint.mintingPleaseWaitSolana', 'Solana transaction in progress. This may take up to 30 seconds...')
                  : t('nft.mint.mintingPleaseWait', 'This may take a moment...')}
              </div>
            </div>
          )}

          {/* Error */}
          {currentStep === 'error' && (
            <div className="max-w-md mx-auto text-center space-y-6 py-12">
              <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <AlertCircle className="w-12 h-12 text-red-600" />
              </div>

              <div>
                <h3 className="text-2xl mb-2">{t('nft.mint.errorTitle', 'Minting Failed')}</h3>
                <p className="text-gray-600 text-sm mb-3">
                  {t('nft.mint.errorSubtitle', 'Something went wrong during the NFT minting process.')}
                </p>
              </div>

              {/* Error details box */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left">
                <p className="text-sm font-medium text-red-800 mb-1">
                  {t('nft.mint.errorDetails', 'Error Details:')}
                </p>
                <p className="text-sm text-red-700">
                  {mintError || t('nft.mint.unknownError', 'An unknown error occurred.')}
                </p>
              </div>

              {/* Helpful tips based on error */}
              {mintError && (mintError.toLowerCase().includes('insufficient') || mintError.toLowerCase().includes('balance') || mintError.toLowerCase().includes('pet')) && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
                  <p className="text-sm font-medium text-amber-800 mb-1">
                    {t('nft.mint.helpTip', 'Tip:')}
                  </p>
                  <p className="text-sm text-amber-700">
                    {t('nft.mint.insufficientBalanceTip', 'Minting requires 50 PETS tokens in your on-chain wallet. Go to Rewards → Claim to move your earned PETS to your Solana wallet first.')}
                  </p>
                </div>
              )}

              {mintError && (mintError.toLowerCase().includes('wallet') || mintError.toLowerCase().includes('keypair')) && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
                  <p className="text-sm font-medium text-amber-800 mb-1">
                    {t('nft.mint.helpTip', 'Tip:')}
                  </p>
                  <p className="text-sm text-amber-700">
                    {t('nft.mint.noWalletTip', 'You need a Petso wallet to mint NFTs. Go to the Rewards section to create one.')}
                  </p>
                </div>
              )}

              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setCurrentStep('mint')}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  {t('common.back', 'Back')}
                </button>
                <button
                  onClick={handleMint}
                  className="px-6 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg hover:opacity-90"
                >
                  {t('nft.mint.retry', 'Try Again')}
                </button>
              </div>
            </div>
          )}

          {/* Success */}
          {currentStep === 'success' && (
            <div className="max-w-2xl mx-auto text-center space-y-6">
              <div className="w-20 h-20 bg-gradient-to-br from-pink-100 to-purple-100 rounded-full flex items-center justify-center mx-auto">
                <Sparkles className="w-12 h-12 text-pink-600" />
              </div>

              <div>
                <h3 className="text-3xl mb-2">{t('nft.mint.successTitle')}</h3>
                <p className="text-gray-600">{t('nft.mint.successSubtitle')}</p>
              </div>

              {/* NFT Preview */}
              <div
                className={`aspect-square max-w-sm mx-auto rounded-xl p-8 ${previewBgClass}`}
                style={
                  Object.keys(previewBgStyle).length
                    ? previewBgStyle
                    : undefined
                }
              >
                <div
                  className={`w-full h-full rounded-lg bg-white flex items-center justify-center overflow-hidden ${
                    frame === 'border'
                      ? 'border-8 border-yellow-400'
                      : frame === 'shadow'
                        ? 'shadow-2xl'
                        : frame === 'neon'
                          ? 'border-4 border-pink-400 shadow-lg shadow-pink-400/50'
                          : ''
                  }`}
                >
                  <div className="text-center w-full h-full flex flex-col items-center justify-center p-4">
                    {displayImageUrl ? (
                      <div className="relative w-32 h-32 sm:w-40 sm:h-40 rounded-xl overflow-hidden mb-3 flex-shrink-0 flex items-center justify-center">
                        {isSticker ? (
                          <div
                            className="rounded-xl overflow-hidden w-full h-full flex items-center justify-center bg-gray-100"
                            style={{
                              filter: 'contrast(1.12)',
                              border: '12px solid white',
                              boxSizing: 'border-box',
                            }}
                          >
                            <img
                              src={displayImageUrl}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : isPixel ? (
                          <div
                            className="overflow-hidden rounded-xl flex-shrink-0"
                            style={{
                              width: 128,
                              height: 128,
                              imageRendering: 'pixelated',
                            }}
                          >
                            <div
                              style={{
                                width: 32,
                                height: 32,
                                transform: 'scale(4)',
                                transformOrigin: 'top left',
                              }}
                            >
                              <img
                                src={displayImageUrl}
                                alt=""
                                style={{
                                  width: 32,
                                  height: 32,
                                  objectFit: 'cover',
                                  display: 'block',
                                }}
                              />
                            </div>
                          </div>
                        ) : (
                          <img
                            src={displayImageUrl}
                            alt=""
                            className="w-full h-full object-cover"
                            style={
                              isSeasonal
                                ? { filter: seasonalImageFilter }
                                : artworkImageStyle
                            }
                          />
                        )}
                        {templateLabel && (
                          <span className="absolute bottom-1 left-1 right-1 bg-black/60 text-white text-xs py-1 rounded text-center">
                            {t(templateLabel)}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="relative inline-block">
                        <div className="text-6xl mb-4">
                          {pet.species === 'dog' ? '🐕' : '🐈'}
                        </div>
                        {templateLabel && (
                          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-2 py-0.5 rounded whitespace-nowrap">
                            {t(templateLabel)}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="text-xl mb-2">{nickname}</div>
                    {caption && (
                      <div className="text-sm text-gray-600 italic">
                        "{caption}"
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-pink-50 border border-pink-200 rounded-full px-4 py-2 inline-block">
                <span className="text-sm text-pink-800">
                  {t('nft.mint.funOnlyBadge')}
                </span>
              </div>

              {/* Transaction Confirmation Details */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-5 text-left space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <h4 className="font-semibold text-green-800">
                    {t('nft.mint.txConfirmed', 'Transaction Confirmed on Solana')}
                  </h4>
                </div>

                {mintResult?.name && (
                  <div>
                    <div className="text-xs text-green-600 uppercase tracking-wide mb-0.5">{t('nft.mint.nftName', 'NFT Name')}</div>
                    <div className="text-sm font-medium text-green-900">{mintResult.name}</div>
                  </div>
                )}

                {mintResult?.nftMintAddress && (
                  <div>
                    <div className="text-xs text-green-600 uppercase tracking-wide mb-0.5">{t('nft.mint.mintAddress', 'Mint Address')}</div>
                    <div className="text-sm font-mono text-green-900 break-all">{mintResult.nftMintAddress}</div>
                  </div>
                )}

                {mintResult?.txSignature && (
                  <div>
                    <div className="text-xs text-green-600 uppercase tracking-wide mb-0.5">{t('nft.mint.txSignature', 'Transaction Signature')}</div>
                    <div className="text-sm font-mono text-green-900 break-all">{mintResult.txSignature}</div>
                  </div>
                )}

                <div>
                  <div className="text-xs text-green-600 uppercase tracking-wide mb-0.5">{t('nft.mint.network', 'Network')}</div>
                  <div className="text-sm text-green-900">{mintResult?.network || 'Solana Devnet'}</div>
                </div>

                <div>
                  <div className="text-xs text-green-600 uppercase tracking-wide mb-0.5">{t('nft.mint.costPaid', 'Cost Paid')}</div>
                  <div className="text-sm font-medium text-green-900">50 PETS</div>
                </div>
              </div>

              {/* Solana Explorer Link - Prominent */}
              {mintResult?.explorerUrl && (
                <a
                  href={mintResult.explorerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:opacity-90 text-center font-medium"
                >
                  <span>🔗</span>
                  {t('nft.mint.viewOnSolanaExplorer', 'View on Solana Explorer')}
                </a>
              )}

              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={() => {
                    if (mintResult?.explorerUrl && navigator.share) {
                      navigator.share({
                        title: mintResult.name || 'My Petso NFT',
                        text: `Check out my fun Petso NFT on Solana!`,
                        url: mintResult.explorerUrl,
                      }).catch(() => {});
                    } else if (mintResult?.explorerUrl) {
                      navigator.clipboard.writeText(mintResult.explorerUrl).catch(() => {});
                    }
                  }}
                  className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                >
                  {t('nft.mint.share')}
                </button>
                <button
                  onClick={() => {
                    setMintResult(null);
                    setMintError(null);
                    setMintSubStep('preparing');
                    setCurrentStep('disclosure');
                  }}
                  className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                >
                  {t('nft.mint.mintAnother')}
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                >
                  {t('nft.mint.backToProfile')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer Navigation */}
        {!['disclosure', 'minting', 'success', 'error'].includes(currentStep) && (
          <div className="border-t border-gray-200 p-6">
            <div className="flex justify-between">
              <button
                onClick={() => {
                  if (currentStep === 'artwork') setCurrentStep('disclosure');
                  else if (currentStep === 'customize')
                    setCurrentStep('artwork');
                  else if (currentStep === 'mint') setCurrentStep('customize');
                }}
                className="flex items-center gap-2 px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <ChevronLeft className="w-4 h-4" />
                {t('nft.mint.back')}
              </button>

              <button
                onClick={() => {
                  if (currentStep === 'artwork') setCurrentStep('customize');
                  else if (currentStep === 'customize') setCurrentStep('mint');
                  else if (currentStep === 'mint') handleMint();
                }}
                disabled={currentStep === 'artwork' && !canProceedFromArtwork}
                className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {currentStep === 'mint'
                  ? t('nft.mint.mintForFunCost', 'Mint for Fun (50 PETS)')
                  : t('nft.mint.next')}
                {currentStep !== 'mint' && <ChevronRight className="w-4 h-4" />}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
